import { useState } from 'react';

import FileSaver from 'file-saver';

import { OrderResult, OrderStatus } from '@customTypes/order';
import { BaseRequisition, BaseRequisitionOrder } from '@customTypes/requisition';
import { getOrderResultsById } from '@store/actions';

function orderResultsIncludePdf(results: OrderResult[]): boolean {
  return results.some(r => r.mimetype === 'application/pdf');
}

export interface OnDownloadOptions {
  onNoReportPdf?: () => void;
}

interface UseDownloadResult {
  downloadError: boolean | string;
  setDownloadError: React.Dispatch<React.SetStateAction<boolean | string>>;
  onDownload: (order: BaseRequisitionOrder, requisitionId: string, options?: OnDownloadOptions) => void;
  onClinicalSummaryLetterDownload: (requisition: BaseRequisition) => void;
}

export const useDownload = (): UseDownloadResult => {
  const [downloadError, setDownloadError] = useState<boolean | string>(false);

  const onDownload = async (order: BaseRequisitionOrder, requisitionId: string, options?: OnDownloadOptions) => {
    try {
      const results = await getOrderResultsById(order.order_id);
      const hasPdf = orderResultsIncludePdf(results);

      if (!hasPdf) {
        if (order.status === OrderStatus.CANCELED && options?.onNoReportPdf) {
          options.onNoReportPdf();
        } else {
          setDownloadError(true);
        }
        return;
      }

      results.forEach((orderResult: OrderResult) => {
        if (orderResult.mimetype === 'application/pdf') {
          fetch(orderResult.uri)
            .then(res => res.blob())
            .then(blob =>
              FileSaver.saveAs(blob, requisitionId + ' - ' + order.product_name.replace(/,/g, '') + '.pdf'),
            );
        }
      });
    } catch (err) {
      setDownloadError(err?.toString() ?? true);
    }
  };

  const onClinicalSummaryLetterDownload = (requisition: BaseRequisition) => {
    try {
      if (requisition.clinical_summary_letter) {
        fetch(requisition.clinical_summary_letter.uri)
          .then(res => res.blob())
          .then(blob => FileSaver.saveAs(blob, `${requisition.requisition_id} - Clinical Summary Letter.pdf`));
      }
    } catch (err) {
      setDownloadError(err?.toString() ?? true);
    }
  };

  return {
    onDownload,
    downloadError,
    setDownloadError,
    onClinicalSummaryLetterDownload,
  };
};
